import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useToggleable as useDrawer } from '@stores/toggleables';
import { useSnackbar } from '@stores/snackbar';
import { getMe } from '@api/selectors/auth';
import { getPlatformSubscriptionGoalUIDs } from '@api/selectors/subscription';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import {
  isPostClickable,
  isCompletedWithNoVideoUrl,
  classDidNotHappen
} from '@models/Item';
import { DRAWER_IDS } from '@constants/index';
import usePlayerStore from '@cont/ClassVideoPlayer/hooks/usePlayerStore';
import {
  fetchClassDetails,
  fetchPreviewDetails,
  getClassInfoV2
} from '@api/fetchers/class';
import { processClassData } from '@api/hooks/class/utils';
import { readCookie } from '@utils/cookies';
import { emptyList } from '@constants/empty';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { abVariations } from '@utils/abHelper';
import { localStorageAvailable } from '@utils/localStorage';
import {
  CLASS_BLOCKER_ID,
  LIMITING_TIME_CONSUMPTION_GOALS
} from '@cont/ClassVideoPlayer/constants';
import { getDailyTimeLimit } from '@api/selectors/classroom';
import useConstants from '@api/hooks/useConstants';
import useClassroomConsumption from '@api/hooks/class/useClassroomConsumption';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import usePurchasedItems from '@api/hooks/auth/usePurchasedItems';

const isPlusLessonClickable = (
  lesson,
  course,
  me,
  platformSubscriptionGoalUIDs,
  gtpGoalsList
) => {
  if (isCompletedWithNoVideoUrl(lesson) || classDidNotHappen(lesson)) {
    return false;
  }

  if (
    course?.isSpecial ||
    gtpGoalsList.includes(course?.goalUid || course?.goal?.uid)
  ) {
    return true;
  }

  return isPostClickable(lesson);
};

const changeWindowURL = (data, slug, path) => {
  window.history.pushState(data, slug, path);
};

const fetchPreviewData = async (courseUID, previewUID) => {
  const accessToken = readCookie('accessToken');
  const classData = await fetchPreviewDetails(
    courseUID,
    previewUID,
    accessToken
  );

  return {
    classData,
    courseData: classData.programme
  };
};

const refetchAllClassRelatedData = async (classUID, goalUID, isFreeLesson) => {
  const accessToken = readCookie('accessToken');
  const classData = isFreeLesson
    ? await getClassInfoV2(classUID, goalUID, true, accessToken)
    : await fetchClassDetails(classUID, accessToken);
  const processedClassData = processClassData(classData);

  return {
    classData: processedClassData,
    courseData: processedClassData.programme
  };
};

const getGoal = (lesson, course) => {
  return lesson.programme?.goal || course.goal;
};

const getLimitedTimeVideoData = async (
  course,
  me,
  setShouldFetchClassConsumptionData
) => {
  const { goal: goalInfo } = course;
  const isGoalForExp = LIMITING_TIME_CONSUMPTION_GOALS.includes(goalInfo?.uid);
  const isFreeLearner = !hasActiveSubscriptionToGoal(me, goalInfo?.uid);
  if (isGoalForExp && isFreeLearner)
    fetchABExperimentData('limiting_consumption_app', me.jwtToken).then(
      (data) => {
        if (data.variation === abVariations.TEST_1)
          setShouldFetchClassConsumptionData(true);
      }
    );
};

const useCourseClassPlayer = () => {
  const route = useRouter();
  const { closeElement: closePlayerDrawer, openElement: openPlayerDrawer } =
    useDrawer(DRAWER_IDS.COURSE_CLASS_PLAYER);
  const { openElement: openPlayerBlocker } = useDrawer(CLASS_BLOCKER_ID);
  const { open: showSnackbar } = useSnackbar();

  const { setCurrentClassData } = usePlayerStore();

  const { constants } = useConstants();
  const me = useMyInfo(getMe);
  const platformSubscriptionGoalUIDs = usePurchasedItems(
    getPlatformSubscriptionGoalUIDs
  );
  const [shouldFetchClassConsumptionData, setShouldFetchClassConsumptionData] =
    useState(false);
  const { classroomConsumption, error: classConsumptionFetchingError } =
    useClassroomConsumption(shouldFetchClassConsumptionData);
  const dailyLimit = getDailyTimeLimit(classroomConsumption?.data);

  const gtpGoalsList = constants?.GTP_GOALS || emptyList;

  const checkIfClassIsClickable = useCallback(
    (currentLesson, currentCourse) => {
      return isPlusLessonClickable(
        currentLesson,
        currentCourse,
        me,
        platformSubscriptionGoalUIDs,
        gtpGoalsList
      );
    },
    [me, gtpGoalsList, platformSubscriptionGoalUIDs]
  );

  const handlePlayerSetUp = ({
    lesson,
    course,
    byPassCheck = false,
    isFreeLesson = false,
    redirectURL = null,
    isExternal = false,
    source = ''
  }) => {
    const { uid, slug } = lesson;
    let freeTime = localStorage.getItem('freeTimeRemaining');
    freeTime = freeTime && parseInt(freeTime, 10);
    const pathToRedirect =
      redirectURL ||
      `/class/${slug}/${uid}${isFreeLesson ? '?isSyllabusFeed=true' : ''}`;

    if (byPassCheck || checkIfClassIsClickable(lesson, course)) {
      const updatedClassData = {
        ...lesson,
        programmeType: lesson.programmeType || course.programmeType || 1,
        ...(!lesson.programme && {
          programme: course
        }),
        ...(!lesson.author && {
          author: course.author || {}
        }),
        source
      };

      setCurrentClassData(updatedClassData);

      if (!isExternal) {
        changeWindowURL({ key: uid }, slug, `${pathToRedirect}`);
      }

      if (freeTime !== null && freeTime <= 0) openPlayerBlocker();
      else openPlayerDrawer();
    }
  };

  useEffect(() => {
    if (classroomConsumption?.data && localStorageAvailable()) {
      const { todayWatchTime, dailyWatchLimit } = classroomConsumption.data;
      localStorage.setItem(
        'freeTimeRemaining',
        dailyWatchLimit - todayWatchTime
      );
    }
  }, [classroomConsumption, classConsumptionFetchingError]);

  const open = useCallback(
    async ({
      lesson,
      course,
      byPassCheck = false,
      isFreeLesson = false,
      forceFetch = false,
      preview = false,
      isExternal = false,
      source = ''
    }) => {
      try {
        const { uid } = lesson;
        if (course?.isSpecial)
          await getLimitedTimeVideoData(
            course,
            me,
            setShouldFetchClassConsumptionData
          );
        if (preview) {
          const { classData: updatedClassData, courseData: updatedCourseData } =
            await fetchPreviewData(course.uid, uid);

          handlePlayerSetUp({
            lesson: { ...updatedClassData, preview: true },
            course: updatedCourseData,
            byPassCheck: true,
            redirectURL: route.asPath
          });

          return;
        }

        const goal = getGoal(lesson, course);

        if (forceFetch) {
          const { classData: updatedClassData, courseData: updatedCourseData } =
            await refetchAllClassRelatedData(uid, goal.uid, isFreeLesson);

          handlePlayerSetUp({
            lesson: updatedClassData,
            course: updatedCourseData,
            byPassCheck,
            isFreeLesson,
            isExternal,
            source
          });

          return;
        }

        handlePlayerSetUp({
          lesson,
          course,
          byPassCheck,
          isFreeLesson,
          isExternal,
          source
        });
      } catch (err) {
        showSnackbar({
          message: 'Unable to play the class. Please try again!',
          type: 'error'
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me, gtpGoalsList, dailyLimit, platformSubscriptionGoalUIDs?.length]
  );

  const close = useCallback(() => {
    closePlayerDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    open,
    close,
    checkIfClassIsClickable
  };
};

export default useCourseClassPlayer;
